import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiSearch, FiFilter, FiUsers, FiCalendar, FiCopy, FiCheck, FiChevronLeft, FiChevronRight, FiExternalLink, FiSettings } from 'react-icons/fi';
import Layout from '../components/Layout';
import { apiService } from '../services/api';
import { GuestCVUpload } from '../types';
import GuestCVDetailModal from '../components/GuestCVDetailModal';

const GuestUsage = () => {
  const [guestCVs, setGuestCVs] = useState<GuestCVUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(20);

  // Stats
  const [totalGuestUsers, setTotalGuestUsers] = useState(0);
  const [maxCreditsConfigured, setMaxCreditsConfigured] = useState<number>(5);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [extensionIdFilter, setExtensionIdFilter] = useState('');

  // Detail modal
  const [selectedCV, setSelectedCV] = useState<GuestCVUpload | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Copy feedback
  const [copiedText, setCopiedText] = useState<string | null>(null);

  // Reset credits
  const [resettingCredits, setResettingCredits] = useState(false);
  const [resetResult, setResetResult] = useState<{message: string; type: 'success' | 'error'} | null>(null);

  // Format date to DD/MM/YYYY HH:MM
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return 'Invalid date';
    }
  };

  const fetchGuestCVs = useCallback(async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const params: any = {
        page: currentPage,
        page_size: pageSize,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (extensionIdFilter) {
        params.extension_id = extensionIdFilter;
      }

      // Handle date filters
      if (dateFilter !== 'all' && dateFilter !== 'custom') {
        const now = new Date();
        let dateFrom = new Date();

        switch (dateFilter) {
          case 'today':
            dateFrom.setHours(0, 0, 0, 0);
            break;
          case 'week':
            dateFrom.setDate(now.getDate() - 7);
            break;
          case 'month':
            dateFrom.setDate(now.getDate() - 30);
            break;
        }

        params.date_from = dateFrom.toISOString();
      } else if (dateFilter === 'custom') {
        if (customDateRange.start) {
          params.date_from = new Date(customDateRange.start).toISOString();
        }
        if (customDateRange.end) {
          params.date_to = new Date(customDateRange.end).toISOString();
        }
      }

      const response = await apiService.getGuestCVs(params);

      if (response && response.success && response.data) {
        setGuestCVs(response.data.results);
        setTotalCount(response.data.count);
        setTotalPages(response.data.total_pages);

        // Calculate unique guest users from all results
        const uniqueGuestUserIds = new Set(
          response.data.results
            .map((cv: any) => cv.guest_session_info?.id)
            .filter((id: any) => id !== null && id !== undefined)
        );
        setTotalGuestUsers(uniqueGuestUserIds.size);
      }
    } catch (error) {
      console.error('Error fetching guest CVs:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentPage, pageSize, searchTerm, extensionIdFilter, dateFilter, customDateRange]);

  // Fetch current configured maximum credits
  const fetchMaxCreditsConfig = useCallback(async () => {
    try {
      const response = await apiService.getMasterVariables();
      if (response.success && response.data) {
        const guestMaxCreditsVar = response.data.find((variable: any) => variable.name === 'GuestUserMaxCredits');
        if (guestMaxCreditsVar) {
          setMaxCreditsConfigured(parseInt(guestMaxCreditsVar.value) || 5);
        }
      }
    } catch (error) {
      console.error('Error fetching master variables:', error);
    }
  }, []);

  useEffect(() => {
    fetchGuestCVs();
    fetchMaxCreditsConfig();
  }, [fetchGuestCVs, fetchMaxCreditsConfig]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [dateFilter, extensionIdFilter]);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const handleResetGuestCredits = async () => {
    if (!window.confirm('Are you sure you want to reset all active guest session credits to the current configured maximum? This will update all guest users.')) {
      return;
    }

    setResettingCredits(true);
    setResetResult(null);

    try {
      const response = await apiService.resetGuestCredits();
      
      if (response.success) {
        const data = response.data;
        setResetResult({
          message: `Successfully reset ${data.sessions_updated} of ${data.total_active_sessions} guest sessions to ${data.new_credit_limit} credits`,
          type: 'success'
        });
        
        // Refresh the data to show updated stats
        fetchGuestCVs();
      } else {
        setResetResult({
          message: response.error?.message || 'Failed to reset guest credits',
          type: 'error'
        });
      }
    } catch (error: any) {
      setResetResult({
        message: error.message || 'An error occurred while resetting credits',
        type: 'error'
      });
    } finally {
      setResettingCredits(false);
      // Clear message after 5 seconds
      setTimeout(() => setResetResult(null), 5000);
    }
  };

  const handleRowClick = async (cv: GuestCVUpload) => {
    try {
      // Fetch complete CV details
      const response = await apiService.getGuestCVDetail(cv.id);
      if (response && response.success && response.data) {
        setSelectedCV(response.data);
        setShowDetailModal(true);
      } else {
        console.error('Failed to fetch CV details');
      }
    } catch (error) {
      console.error('Error fetching CV details:', error);
    }
  };

  const getScoreBadgeColor = (score: number | null): string => {
    if (!score) return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    if (score >= 80) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    if (score >= 60) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Guest Usage</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Monitor and manage guest user activity
            </p>
          </div>
          
          <button
            onClick={handleResetGuestCredits}
            disabled={resettingCredits}
            className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-400 dark:hover:bg-brand-300"
          >
            <FiRefreshCw className={`h-4 w-4 ${resettingCredits ? 'animate-spin' : ''}`} />
            {resettingCredits ? 'Resetting...' : 'Reset Guest Credits'}
          </button>
        </div>

        {/* Reset Result Notification */}
        {resetResult && (
          <div className={`rounded-lg p-4 ${
            resetResult.type === 'success' 
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
          }`}>
            <p className="text-sm font-medium">{resetResult.message}</p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <div className="rounded-lg bg-white p-6 shadow dark:bg-navy-800">
            <div className="flex items-center">
              <div className="rounded-full bg-purple-100 p-3 dark:bg-purple-900/30">
                <FiUsers className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Guest Users</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalGuestUsers}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg bg-white p-6 shadow dark:bg-navy-800">
            <div className="flex items-center">
              <div className="rounded-full bg-blue-100 p-3 dark:bg-blue-900/30">
                <FiCalendar className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total CVs Analyzed</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalCount}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg bg-white p-6 shadow dark:bg-navy-800">
            <div className="flex items-center">
              <div className="rounded-full bg-green-100 p-3 dark:bg-green-900/30">
                <FiSettings className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Max Credits Configured</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{maxCreditsConfigured}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">per guest user</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="rounded-lg bg-white p-6 shadow dark:bg-navy-800">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {/* Search */}
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by fingerprint, extension ID, name..."
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              />
            </div>

            {/* Date Filter */}
            <div>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Extension ID Filter */}
            <div>
              <input
                type="text"
                value={extensionIdFilter}
                onChange={(e) => setExtensionIdFilter(e.target.value)}
                placeholder="Filter by Extension ID..."
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              />
            </div>
          </div>

          {/* Custom Date Range */}
          {dateFilter === 'custom' && (
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">From Date</label>
                <input
                  type="date"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">To Date</label>
                <input
                  type="date"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-navy-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-navy-700">
              <thead className="bg-gray-50 dark:bg-navy-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Date/Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Candidate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Fingerprint
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Credits
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-navy-700 dark:bg-navy-800">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <FiRefreshCw className="h-6 w-6 animate-spin text-brand-500" />
                        <span className="text-gray-500 dark:text-gray-400">Loading guest CVs...</span>
                      </div>
                    </td>
                  </tr>
                ) : guestCVs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      No guest CVs found. Try adjusting your filters.
                    </td>
                  </tr>
                ) : (
                  guestCVs.map((cv) => (
                    <tr
                      key={cv.id}
                      onClick={() => handleRowClick(cv)}
                      className="cursor-pointer transition hover:bg-gray-50 dark:hover:bg-navy-700"
                    >
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                        #{cv.id}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(cv.analyzed_at)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="font-medium text-gray-900 dark:text-white">{cv.candidate_name}</div>
                        <div className="text-gray-500 dark:text-gray-400">{cv.email || 'No email'}</div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getScoreBadgeColor(cv.score)}`}>
                          {cv.score !== null ? `${cv.score}%` : 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center space-x-2">
                          <span className="truncate max-w-[100px] text-gray-500 dark:text-gray-400">
                            {cv.fingerprint ? cv.fingerprint.substring(0, 12) + '...' : 'N/A'}
                          </span>
                          {cv.fingerprint && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopy(cv.fingerprint!, `fingerprint-${cv.id}`);
                              }}
                              className="text-gray-400 hover:text-brand-500"
                            >
                              {copiedText === `fingerprint-${cv.id}` ? (
                                <FiCheck className="h-4 w-4 text-green-500" />
                              ) : (
                                <FiCopy className="h-4 w-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {cv.guest_session_info ? (
                          <span>
                            {cv.guest_session_info.used_credits} / {cv.guest_session_info.total_credits}
                          </span>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm">
                        {cv.source_url && (
                          <a
                            href={cv.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-brand-500 hover:text-brand-600"
                            title="View source"
                          >
                            <FiExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 dark:border-navy-700 dark:bg-navy-800 sm:px-6">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * pageSize, totalCount)}</span> of{' '}
                    <span className="font-medium">{totalCount}</span> results
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 dark:border-navy-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                    >
                      <FiChevronLeft className="h-5 w-5" />
                    </button>
                    <span className="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 dark:border-navy-600 dark:bg-navy-700 dark:text-white">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 dark:border-navy-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                    >
                      <FiChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedCV && (
        <GuestCVDetailModal
          cv={selectedCV}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedCV(null);
          }}
        />
      )}
    </Layout>
  );
};

export default GuestUsage;
